import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Alert,
  useAbsolutePriceDropDialog,
  useManualPriceDisableAutoPricingDialog,
  useRelativeFloorPriceDropDialog,
  useRelativePriceDropDialog,
} from 'src/dialogs/AlertWithSuppressionDialog';
import { useManualPriceAdjustFloorOrCeilingDialog } from 'src/dialogs/AlertWithSuppressionDialog/useManualPriceAdjustFloorOrCeilingDialog';
import { isDatePassedHours } from 'src/utils/dateTimeUtils';
import {
  isAbsolutePriceDrop,
  isRelativeFloorPriceDrop,
  isRelativePriceDrop,
} from 'src/utils/inventoryUtils';
import {
  Feature,
  ListingDetails,
  ListingDetailsUpdateInput,
} from 'src/WebApiController';

import { useConfirmationDialogSettings } from './api/useConfirmationDialogSettings';
import { useBasicDialog } from './useBasicDialog';
import { useUserHasFeature } from './useUserHasFeature';

export const usePriceChangeDialogs = (
  viagogoVirtualId: string | undefined,
  listing: ListingDetails | undefined | null,
  isBulkEdit?: boolean
) => {
  const { confirmationDialogSettings } = useConfirmationDialogSettings();
  const warningDialog = useBasicDialog();

  const hasSellerEventAlertMutes = useUserHasFeature(
    Feature.SellerEventAlertMutes
  );
  const hasRemovePriceIncreaseRoadbump = useUserHasFeature(
    Feature.RemovePriceIncreaseRoadbump
  );
  const { watch, setValue, register, getValues, formState } =
    useFormContext<ListingDetailsUpdateInput>();

  const listPrice = watch('listPrice');
  const marketplacePriceUpdates = watch('marketplacePriceUpdates');
  const autoPricingEnabled = watch('autoPricingEnabled');
  const netProceedsFloor = watch('netProceedsFloor');
  const netProceedsCeiling = watch('netProceedsCeiling');

  const manualPriceDisableAutoPricingProps =
    useManualPriceDisableAutoPricingDialog(() => {
      setValue('autoPricingEnabled', false);
    }, viagogoVirtualId);

  const marketplacePrices = marketplacePriceUpdates
    .filter((m) => m.priceByMarketplace)
    .map((m) => m.listPrice);
  const allListPrices = useMemo(() => {
    const p = [listPrice, ...marketplacePrices]
      .filter((n) => n != null)
      .map((n) => n!);

    return p;
  }, [listPrice, marketplacePrices]);

  const smallestListPrice = useMemo(
    () => Math.min(...allListPrices.filter((l) => l > 0)),
    [allListPrices]
  );
  const largestListPrice = useMemo(
    () => Math.max(...allListPrices.filter((l) => l > 0)),
    [allListPrices]
  );
  const manualPriceAdjustFloorProps = useManualPriceAdjustFloorOrCeilingDialog(
    smallestListPrice,
    netProceedsFloor,
    netProceedsCeiling,
    () => {
      if (smallestListPrice < (netProceedsFloor ?? Number.MIN_VALUE)) {
        setValue('netProceedsFloor', smallestListPrice);
      }
    },
    () => {
      if (
        listPrice != null &&
        listPrice > 0 &&
        listPrice < (netProceedsFloor ?? Number.MIN_VALUE)
      ) {
        setValue('listPrice', netProceedsFloor);
      }

      marketplacePriceUpdates.forEach((m, i) => {
        if (m.priceByMarketplace && m.listPrice != null && m.listPrice > 0) {
          if (m.listPrice < (netProceedsFloor ?? 0)) {
            setValue(
              `marketplacePriceUpdates.${i}.listPrice`,
              netProceedsFloor
            );
          }
        }
      });
    },
    viagogoVirtualId
  );

  const manualPriceAdjustCeilingProps =
    useManualPriceAdjustFloorOrCeilingDialog(
      largestListPrice,
      netProceedsFloor,
      netProceedsCeiling,
      () => {
        if (largestListPrice > (netProceedsCeiling ?? Number.MAX_VALUE)) {
          setValue('netProceedsCeiling', largestListPrice);
        }
      },
      () => {
        if (
          listPrice != null &&
          listPrice > 0 &&
          listPrice > (netProceedsCeiling ?? Number.MAX_VALUE)
        ) {
          setValue('listPrice', netProceedsCeiling);
        }

        marketplacePriceUpdates.forEach((m, i) => {
          if (m.priceByMarketplace && m.listPrice != null && m.listPrice > 0) {
            if (m.listPrice > (netProceedsCeiling ?? Number.MAX_VALUE)) {
              setValue(
                `marketplacePriceUpdates.${i}.listPrice`,
                netProceedsCeiling
              );
            }
          }
        });
      },
      viagogoVirtualId
    );

  const absolutePriceDropProps = useAbsolutePriceDropDialog(
    () => {
      if (smallestListPrice < (netProceedsFloor ?? Number.MIN_VALUE)) {
        setValue('netProceedsFloor', smallestListPrice);
      }
    },
    () => {
      setValue('listPrice', formState.defaultValues?.listPrice ?? null);
    },
    viagogoVirtualId
  );

  const relativePriceDropProps = useRelativePriceDropDialog(
    () => {
      if (smallestListPrice < (netProceedsFloor ?? Number.MIN_VALUE)) {
        setValue('netProceedsFloor', smallestListPrice);
      }
    },
    () => {
      setValue('listPrice', formState.defaultValues?.listPrice ?? null);
    },
    viagogoVirtualId
  );

  const relativeFloorPriceDropProps = useRelativeFloorPriceDropDialog(
    () => {
      if (smallestListPrice < (netProceedsFloor ?? Number.MIN_VALUE)) {
        setValue('netProceedsFloor', smallestListPrice);
      }
    },
    () => {
      setValue('listPrice', formState.defaultValues?.listPrice ?? null);
    },
    viagogoVirtualId
  );

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const getFloorAlerts = useCallback((): Alert[] => {
    const isFloorCeilingDialogEnabled =
      confirmationDialogSettings?.settings?.FloorCeilingViolation?.isActive ??
      true;

    if (
      isFloorCeilingDialogEnabled &&
      smallestListPrice < (netProceedsFloor ?? Number.MIN_VALUE)
    ) {
      if (isDatePassedHours(manualPriceAdjustFloorProps.lastTimeStamp, 1)) {
        return [manualPriceAdjustFloorProps];
      }
    }
    return [];
  }, [
    confirmationDialogSettings,
    manualPriceAdjustFloorProps,
    netProceedsFloor,
    smallestListPrice,
  ]);

  const getCeilingAlerts = useCallback((): Alert[] => {
    const isFloorCeilingDialogEnabled =
      confirmationDialogSettings?.settings?.FloorCeilingViolation?.isActive ??
      true;

    if (
      isFloorCeilingDialogEnabled &&
      largestListPrice > (netProceedsCeiling ?? Number.MAX_VALUE)
    ) {
      if (isDatePassedHours(manualPriceAdjustCeilingProps.lastTimeStamp, 1)) {
        return [manualPriceAdjustCeilingProps];
      }
    }
    return [];
  }, [
    confirmationDialogSettings,
    largestListPrice,
    manualPriceAdjustCeilingProps,
    netProceedsCeiling,
  ]);

  const getAutoPriceAlerts = useCallback((): Alert[] => {
    const isAutoPricingDisableDialogEnabled =
      confirmationDialogSettings?.settings?.AutoPricingDisable?.isActive ??
      true;

    if (
      isAutoPricingDisableDialogEnabled &&
      autoPricingEnabled &&
      isDatePassedHours(manualPriceDisableAutoPricingProps.lastTimeStamp, 1)
    ) {
      return [manualPriceDisableAutoPricingProps];
    }
    return [];
  }, [
    confirmationDialogSettings,
    autoPricingEnabled,
    manualPriceDisableAutoPricingProps,
  ]);

  const getAbsolutePriceDropAlerts = useCallback((): Alert[] => {
    const isAbsolutePriceDropEnabled =
      confirmationDialogSettings?.settings?.AbsolutePriceDrop?.isActive ?? true;

    const absoluteThreshold =
      confirmationDialogSettings?.settings?.AbsolutePriceDrop
        ?.absoluteThreshold;

    if (
      isAbsolutePriceDropEnabled &&
      isAbsolutePriceDrop(
        listPrice,
        formState?.defaultValues?.listPrice,
        absoluteThreshold
      ) &&
      isDatePassedHours(absolutePriceDropProps.lastTimeStamp, 1)
    ) {
      return [absolutePriceDropProps];
    }
    return [];
  }, [
    confirmationDialogSettings,
    listPrice,
    formState?.defaultValues?.listPrice,
    absolutePriceDropProps,
  ]);

  const getRelativePriceDropAlerts = useCallback((): Alert[] => {
    const isRelativePriceDropEnabled =
      confirmationDialogSettings?.settings?.RelativePriceDrop?.isActive ?? true;

    const relativeThreshold =
      confirmationDialogSettings?.settings?.RelativePriceDrop
        ?.relativeThreshold ?? 25;

    if (
      isRelativePriceDropEnabled &&
      isRelativePriceDrop(
        listPrice,
        formState?.defaultValues?.listPrice,
        relativeThreshold
      ) &&
      isDatePassedHours(relativePriceDropProps.lastTimeStamp, 1)
    ) {
      return [relativePriceDropProps];
    }
    return [];
  }, [
    confirmationDialogSettings,
    listPrice,
    formState?.defaultValues?.listPrice,
    relativePriceDropProps,
  ]);

  const getRelativeFloorPriceDropAlerts = useCallback((): Alert[] => {
    // Check if floor relative to unit cost price drop dialog is enabled (default to true if not set)
    const isRelativeFloorPriceDropEnabled =
      confirmationDialogSettings?.settings?.RelativeFloorPriceDrop?.isActive ??
      true;

    const unitCost = listing?.unitCst;

    if (
      isRelativeFloorPriceDropEnabled &&
      isRelativeFloorPriceDrop(listPrice, unitCost?.amt ?? null) &&
      isDatePassedHours(relativeFloorPriceDropProps.lastTimeStamp, 1)
    ) {
      return [relativeFloorPriceDropProps];
    }
    return [];
  }, [
    confirmationDialogSettings,
    listPrice,
    listing?.unitCst,
    relativeFloorPriceDropProps,
  ]);

  const onPriceChanged = useCallback(() => {
    if (listPrice === formState?.defaultValues?.listPrice) {
      return;
    }
    const newAlerts = [
      ...getFloorAlerts(),
      ...getCeilingAlerts(),
      ...getAutoPriceAlerts(),
    ];

    if (hasSellerEventAlertMutes && hasRemovePriceIncreaseRoadbump) {
      newAlerts.push(
        ...getAbsolutePriceDropAlerts(),
        ...getRelativePriceDropAlerts(),
        ...getRelativeFloorPriceDropAlerts()
      );
    }

    if (!isBulkEdit && newAlerts.length) {
      setAlerts(newAlerts);
      warningDialog.launchDialog();
    }
  }, [
    listPrice,
    formState?.defaultValues?.listPrice,
    getFloorAlerts,
    getCeilingAlerts,
    getAutoPriceAlerts,
    getAbsolutePriceDropAlerts,
    getRelativePriceDropAlerts,
    getRelativeFloorPriceDropAlerts,
    hasSellerEventAlertMutes,
    hasRemovePriceIncreaseRoadbump,
    isBulkEdit,
    warningDialog,
  ]);

  return {
    onPriceChanged,
    alerts,
    warningDialog,
  };
};
